import classNames from 'classnames';
import { HTTPError } from 'ky';
import { Tag, ClickableTile, InlineLoading } from '@carbon/react';
import { Checkmark, Warning, Launch } from '@carbon/react/icons';
import { PageHeader } from 'components/page-header';
import { useAdminHealth } from './hooks';
import { linkSections, type LinkSection } from './links';
import type { AdminDashboardWindow } from './types';
import styles from './page.module.scss';

declare let window: AdminDashboardWindow;

/**
 * The health check's state: checking, healthy, or why it is not.
 * @return {JSX.Element} The health indicator
 */
function HealthIndicator() {
  const { data, error, isPending } = useAdminHealth();

  if (isPending) return <InlineLoading description="Checking health..." />;

  if (error) {
    return (
      <div className={styles.healthStatus}>
        <Warning className={styles.warningIcon} />
        <span className={styles.healthError}>
          {error instanceof HTTPError
            ? 'Health check failed'
            : 'Unable to reach health endpoint'}
        </span>
      </div>
    );
  }

  if (data.status === 'ok') {
    return (
      <div className={styles.healthStatus}>
        <Checkmark className={styles.checkIcon} />
        <span className={styles.healthOk}>System Healthy</span>
      </div>
    );
  }

  return (
    <div className={styles.healthStatus}>
      <Warning className={styles.warningIcon} />
      <span className={styles.healthError}>System Error</span>
    </div>
  );
}

interface LinkSectionTilesProps {
  section: LinkSection;
  environmentLabel: string;
}

/**
 * One titled grid of link tiles; a disabled tile says it is off here.
 * @param {LinkSectionTilesProps} props - The section and the environment's name
 * @return {JSX.Element} The section
 */
function LinkSectionTiles({
  section,
  environmentLabel,
}: LinkSectionTilesProps) {
  const {
    icon,
    title,
    links,
    disabledLabel = 'Disabled',
    showLaunchIcon = false,
  } = section;

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>
        {icon}
        {title}
      </h3>
      <div className={styles.linkGrid}>
        {links.map((link) => (
          <ClickableTile
            key={link.label}
            href={link.href || '#'}
            {...{
              target: '_blank',
              rel: 'noopener noreferrer',
              disabled: !link.enabled,
            }}
            className={classNames(styles.linkTile, {
              [styles.linkTileDisabled]: !link.enabled,
            })}
          >
            <div className={styles.linkContent}>
              <div className={styles.linkHeader}>
                <span className={styles.linkLabel}>
                  {link.label}
                  {showLaunchIcon && link.enabled && (
                    <Launch className={styles.externalIcon} />
                  )}
                </span>
                {!link.enabled && (
                  <Tag type="gray" size="sm">
                    {disabledLabel} in {environmentLabel}
                  </Tag>
                )}
              </div>
              {link.description && (
                <p className={styles.linkDescription}>{link.description}</p>
              )}
            </div>
          </ClickableTile>
        ))}
      </div>
    </section>
  );
}

/**
 * Central hub for system administration, monitoring, and diagnostics.
 * @return {JSX.Element} The dashboard
 */
export function AdminDashboardPage() {
  const config = window.CONFIG.adminDashboard;
  const bulkDedupeEnabled = window.CONFIG.featureFlags?.bulkDedupe ?? false;

  return (
    <>
      <PageHeader breadcrumbs={[]} title="Admin Dashboard" actions={null} />
      <div className={styles.pageContent}>
        <p className={styles.description}>
          Central hub for system administration, monitoring, and diagnostics.
          Primarily intended for developer use.
        </p>
        <div className={styles.headerSection}>
          <div className={styles.environmentSection}>
            <span className={styles.label}>Environment:</span>
            <Tag type={config.environment.variant} size="md">
              {config.environment.label}
            </Tag>
          </div>
          <div className={styles.healthSection}>
            <HealthIndicator />
          </div>
        </div>

        {linkSections(config, bulkDedupeEnabled).map((section) => (
          <LinkSectionTiles
            key={section.title}
            section={section}
            environmentLabel={config.environment.label}
          />
        ))}
      </div>
    </>
  );
}
